import { css } from '@emotion/react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { PersonsV3 } from '@frontend/api-person';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AdaptoActions, FloatingActionBar } from '@frontend/adapto-actions';
import { ActionsUI, Actions } from '@frontend/contact-actions';
import { FirstWordBoldText } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { Photos } from '@frontend/photos';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ListRow, Text } from '@frontend/design-system';
import { checkIsNonAttendedEventStatus } from '../../../components/calendar-view/utils';
import { useScheduleActionsContainerMethod } from '../../../context/ScheduleActionsContainerContext';
import { ExtendedAppointment, useGetAppointmentDetails, useGetEntityArchiveStatus } from '../../../hooks';
import { getTimeString } from '../../../utils/date-helpers';
import { AppointmentBadgeStatus } from './AppointmentBadgeStatus';

const TRACKING_ID = 'schedule-pulse:appointment-list:action-menu';

type PatientListRowProps = {
  appt?: ExtendedAppointment;
  optOutText?: boolean;
  locationId: string;
  shouldShowAppointmentModal?: boolean;
  appointments?: Appointment[];
  shouldShowAppointmentStatus?: boolean;
  shouldShowActionMenu?: boolean;
};

export const PatientListRow = ({
  optOutText,
  appt,
  locationId,
  shouldShowAppointmentModal = false,
  appointments,
  shouldShowAppointmentStatus = true,
  shouldShowActionMenu = false,
}: PatientListRowProps) => {
  const { t } = useTranslation('schedule-pulse');

  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  const start = getTimeString(appt?.start);
  const duration = appt?.duration ? `(${appt.duration} Min)` : '';
  const timeDisplay = start ? `${start}${duration ? ` ${duration}` : ''}` : '-';

  const { isAppointmentTypeArchived, isProviderArchived } = useGetEntityArchiveStatus({
    appointmentTypeId: appt?.appointmentTypeId || '',
    providerId: appt?.providerIds?.[0] || '',
  });

  const { closeModal: closeTrayModal } = useScheduleActionsContainerMethod();
  const isTrayModalOpen = useMatchMedia({ maxWidth: breakpoints.large.max });

  const personId = appt?.person?.personId || '';

  const { data: personData } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId,
      locationIds: [locationId],
    },
    {
      enabled: !!personId,
      select: (data) => PersonsV3.PersonHelpers.convertPersonV3ToPerson(data),
    }
  );

  const { triggerProps: viewAppointmentTriggerProps, Modal: AppointmentDetailsModal } = useGetAppointmentDetails({
    appointmentId: appt?.id || '',
    locationId: locationId || appt?.locationId || '',
    appointments,
    enabled: shouldShowAppointmentModal,
  });

  const { handleCallActionClick } = ActionsUI.actions.usePhoneCallAction({
    context: {
      personId: appt?.person?.personId ?? '',
      phoneNumber: appt?.person?.mobilePhone ?? '',
      locationId: appt?.locationId,
    },
  });

  const handleOpenAppointmentModal = () => {
    if (shouldShowAppointmentModal) viewAppointmentTriggerProps?.onClick();
  };

  const handleSetPersonIdAndLocationId = (appointment?: Appointment) => {
    if (appointment?.person?.personId && appointment?.locationId) {
      const personId = appointment.person.personId;
      const locationId = appointment.locationId;
      setPersonId(personId, true, locationId);
    }
  };

  const shouldShowOptOutText = optOutText;

  const gridTemplateAreas = `
  "name name name name"
  "time time ${appt?.practitionerName ? 'practitioner practitioner' : 'time time'}"
  "type type ${appt?.operatory ? 'operatory operatory' : 'type type'}"
`;

  const isNonAttendedV3Event = isScheduleV3FlagEnabled
    ? checkIsNonAttendedEventStatus(appt?.statusOfficeView as EventStatus)
    : false;

  return (
    <FloatingActionBar disabled={!shouldShowActionMenu}>
      <FloatingActionBar.Actions>
        <>
          <Actions.Message
            context={{ personId: personData?.PersonID, locationId }}
            label={t('Send Message')}
            trackingIdSuffix={`${TRACKING_ID}:message`}
            onClick={() => {
              // Temporary fix: The message thread opens underneath Schedule Pulse when Schedule Pulse is a tray modal
              if (closeTrayModal && isTrayModalOpen) {
                closeTrayModal();
              }
            }}
          />
          <Actions.Call
            context={{ personId: personData?.PersonID, locationId }}
            label={t('Click to Call')}
            trackingIdSuffix={`${TRACKING_ID}:call`}
          />
          <Actions.Payments
            context={{ person: personData, personId: personId, locationId }}
            label={t('Request Payment')}
            trackingIdSuffix={`${TRACKING_ID}:request-payment`}
          />
          {personData && (
            <Actions.SendForm
              label={t('Send Form')}
              context={{ person: personData, locationId }}
              trackingIdSuffix={`${TRACKING_ID}:send-form`}
            />
          )}
        </>
      </FloatingActionBar.Actions>
      <FloatingActionBar.Content>
        {AppointmentDetailsModal}
        <ListRow css={{ border: 0, padding: theme.spacing(1, 2) }} onClick={handleOpenAppointmentModal}>
          <ListRow.Lead css={{ alignItems: 'start', paddingRight: theme.spacing(1) }}>
            <Photos.ContactProfilePhoto
              size='small'
              personId={(appt?.person && appt.person?.personId) ?? ''}
              firstName={appt?.person?.firstName}
              lastName={appt?.person?.lastName}
              onClick={() => handleSetPersonIdAndLocationId(appt)}
            />
          </ListRow.Lead>
          <ListRow.Content
            css={{
              display: 'grid',
              gridTemplateAreas: gridTemplateAreas,
              gridTemplateColumns: 'repeat(4, 1fr)',
              gridTemplateRows: 'repeat(3, auto)',
              gap: theme.spacing(0.5),
            }}
          >
            <FirstWordBoldText css={{ textAlign: 'left', gridArea: 'name' }}>
              {getFullName(appt?.person ?? {}) || t('Unknown')}
            </FirstWordBoldText>
            <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'time' }]}>{timeDisplay}</Text>
            {appt?.practitionerName && (
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                {isProviderArchived && <Icon name='archive-small' color='subdued' />}
                <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'practitioner' }]}>
                  {appt?.practitionerName}
                </Text>
              </div>
            )}
            <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'type' }]}>
              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                {isAppointmentTypeArchived && <Icon name='archive-small' color='subdued' />}
                {appt?.type ?? '-'}
              </div>
            </Text>
            {appt?.operatory && (
              <Text css={[textStyles(isNonAttendedV3Event), { gridArea: 'operatory' }]}>{appt?.operatory}</Text>
            )}
          </ListRow.Content>
          <ListRow.Trail
            css={{
              justifySelf: 'end',
              gap: theme.spacing(1),
              paddingLeft: theme.spacing(0.5),
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            {shouldShowOptOutText && (
              <AdaptoActions size='small' iconColor='subdued' variant='action-buttons'>
                <AdaptoActions.Action
                  icon='phone'
                  hoverLabel='Click to Call'
                  label={t('Click to Call')}
                  color='icon'
                  onClick={handleCallActionClick}
                />
              </AdaptoActions>
            )}
            {shouldShowAppointmentStatus && (
              <AppointmentBadgeStatus status={appt?.statusOfficeView ?? ''} appointmentId={appt?.id ?? ''} iconOnly />
            )}
          </ListRow.Trail>
        </ListRow>
      </FloatingActionBar.Content>
    </FloatingActionBar>
  );
};

const textStyles = (isNonAttendedV3Event: boolean) => css`
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${theme.colors.neutral70};
  font-size: ${theme.fontSize(12)};
  text-decoration: ${isNonAttendedV3Event ? 'line-through' : 'none'};
`;
